import { useState, useLayoutEffect } from "react";
import { fromMaybe } from "sanctuary";
import bigDecimal from "js-big-decimal";
import {
  AGENCY_ADMIN,
  CB_ADMIN,
  CITYBASE_ADMIN,
  CLIENT_ADMIN,
  NO_ROLE,
  OKTA_AGENCY_ADMIN,
  OKTA_CLIENT_ADMIN,
  RESEARCHER,
  SUPERVISOR,
  CASHIER,
} from "../sanctuary/typedefs";

export const noop = () => {};

export const PAYMENT_TYPES = {
  CARD: "credit_card",
  CHECK: "check",
  CASH: "cash",
  EXT_CARD: "externally_processed_card",
  ENCRYPT_CARD: "encrypted_card",
  EXT_READ_CARD: "external_read_credit_card", // credit card processed entirely through 3rd party service
  WIRE_PAYMENT: "wire_payment",
};

export const CARD_PAYMENT_TYPES = [
  PAYMENT_TYPES.CARD,
  PAYMENT_TYPES.EXT_CARD,
  PAYMENT_TYPES.EXT_READ_CARD,
  PAYMENT_TYPES.ENCRYPT_CARD,
];

export const toSentenceCase = (str) =>
  str?.charAt?.(0)?.toUpperCase() + str?.slice?.(1)?.toLowerCase();

/**
 * Converts a string into title case, ignoring prepositions unless they are the first word.
 *
 * @param {string} str - A string to be converted.
 * @param {string} [delimiter="_"] - The string delimiter separating words in `str`.
 * @returns {string} - A string formatted in sentence-style title case.
 *
 * Examples:
 * toTitleCase("city of detroit") => "City of Detroit"
 * toTitleCase("parks and Recreation") => "Parks and Recreation"
 * toTitleCase("New york city Police department") => "New York City Police Department"
 * toTitleCase("the department of Finance") => "The Department of Finance"
 * toTitleCase("the-department-of-Finance", "-") => "The Department of Finance"
 * toTitleCase("tom's-agency", "-") => "Tom's Agency"
 */
export const toTitleCase = (str, delimiter = "_") => {
  const title = typeof str === "string" ? str : "";

  const prepositions = new Set([
    "a",
    "an",
    "and",
    "at",
    "by",
    "for",
    "in",
    "of",
    "on",
    "or",
    "the",
    "to",
    "with",
  ]);

  return title
    .toLowerCase()
    .split(delimiter)
    .join(" ")
    .replace(/(?<!['’])\b\S/g, (text) => text.toUpperCase())
    .split(" ")
    .map((word, index) => {
      const lower = word.toLowerCase();
      return index !== 0 && prepositions.has(lower) ? lower : word;
    })
    .join(" ");
};

export const isOverflowing = (ref) => {
  // eslint-disable-next-line
  const [isOverflow, setIsOverflow] = useState(undefined);
  // eslint-disable-next-line
  useLayoutEffect(() => {
    const { current } = ref;
    const trigger = () => {
      const hasOverflow = current.scrollWidth > current.clientWidth;
      setIsOverflow(hasOverflow);
    };

    const mutationObserver = new MutationObserver(trigger);
    if (current) {
      trigger();
      mutationObserver.observe(current, { characterData: true, subtree: true });
    }

    return () => {
      if (current) {
        mutationObserver.disconnect();
      }
    };
  }, [ref]);

  return isOverflow;
};

const formatMoneyString = (s) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(s);

const convertCentsToMoneyDecimal = (amount) => {
  const amountInDecimal = bigDecimal.divide(amount?.toString(), "100");
  return Number(amountInDecimal).toFixed(2);
};

export const amountToCents = (amount) => {
  const amountInCents = bigDecimal.multiply(amount?.toString(), "100");
  return BigInt(Number(amountInCents).toFixed(0));
};

export const displayCurrency = (cents) =>
  formatMoneyString(convertCentsToMoneyDecimal(cents));

export const buildObligationValues = (lineItem, customAttributes) => {
  if (!Array.isArray(lineItem)) {
    return (
      customAttributes?.find((attr) => attr.key === lineItem)?.value ?? lineItem
    );
  }
  const obligationValues = lineItem.map((value) => {
    const matchRegex = /^%(.*%)?$/gim;
    if (matchRegex.test(value)) {
      const formattedValue = value.replace(/%/g, "");
      const obligationValue = customAttributes?.find(
        (attr) => fromMaybe("")(attr.key) === formattedValue
      )?.value;
      return obligationValue?.value;
    }
    return value;
  });
  return obligationValues.every((val) => !!val) ? obligationValues.join(" ") : null;
};

/**
 * Looks up a single custom-attribute value on a line item by key.
 * Mirrors how Sanctuary-deserialized customAttributes are accessed inside
 * `buildObligationValues` above.
 *
 * @param {Array} customAttributes - The line item's customAttributes array.
 * @param {string} key - The attribute key to look up.
 * @returns {string|null} The string value, or null if missing / empty.
 */
export const getCustomAttributeValue = (customAttributes, key) => {
  const match = customAttributes?.find((attr) => fromMaybe("")(attr.key) === key);
  return match?.value?.value || null;
};

/**
 * Resolves a line item's display description and sub-description using a
 * two-tier strategy that mirrors the checkout/confirmation page (NFE):
 *
 *   1. If the obligation has FCS-configured `paymentAttributes.description` /
 *      `subDescription` templates AND they resolve cleanly against this line
 *      item's customAttributes, use the templated values.
 *   2. Otherwise fall back to the line item's `line_item_description` /
 *      `line_item_sub_description` custom attributes directly. These are the
 *      keys NFE (confirmation page) and the payment-reporting webhook already
 *      read, so this fallback works for any agency whose line items carry
 *      these attributes (e.g. Accela-backed agencies) without requiring a
 *      per-obligation FCS template.
 *
 * Returning `null` from either field signals to the consumer to render its
 * own placeholder (e.g. "Item <id>" / "Not Available").
 *
 * @param {Array} customAttributes - The line item's customAttributes array.
 * @param {Array|null} descriptionKeys - FCS description template, or null.
 * @param {Array|null} subdescriptionKeys - FCS subDescription template, or null.
 * @returns {{description: string|null, subDescription: string|null}}
 */
export const resolveLineItemDescriptions = (
  customAttributes,
  descriptionKeys,
  subdescriptionKeys
) => ({
  description:
    buildObligationValues(descriptionKeys, customAttributes) ||
    getCustomAttributeValue(customAttributes, "line_item_description"),
  subDescription:
    buildObligationValues(subdescriptionKeys, customAttributes) ||
    getCustomAttributeValue(customAttributes, "line_item_sub_description"),
});

export const formatLineItems = (
  paymentProcessTypeName,
  lineItems,
  descriptionKeys,
  subdescriptionKeys
) =>
  lineItems.map((lineItem) => {
    const { description, subDescription } = resolveLineItemDescriptions(
      lineItem.customAttributes,
      descriptionKeys,
      subdescriptionKeys
    );
    return {
      description,
      subDescription,
      amount:
        paymentProcessTypeName === "refund"
          ? lineItem.amountRefundedInCents?.value
          : lineItem.amountInCents?.value,
    };
  });

export const roleFixture = {
  [CLIENT_ADMIN]: "client admin",
  [AGENCY_ADMIN]: "agency admin",
  [OKTA_AGENCY_ADMIN]: "agency admin",
  [SUPERVISOR]: "supervisor",
  [RESEARCHER]: "researcher",
  [CITYBASE_ADMIN]: "CB Admin",
  [CB_ADMIN]: "CB Admin",
  [OKTA_CLIENT_ADMIN]: "client admin",
  [NO_ROLE]: "no role or access",
  [CASHIER]: "cashier",
};

export const formatRole = (role) => roleFixture[role] || "role";

/**
 * Builds the agencies array depending on the user's role and pos_role
 * @param {string[]} agencies - array of agency slugs or names
 * @param {string} role - revm role for the user
 * @param {string} pos_role - pos_role for the user
 * @return {string[]} - an array of agency slugs
 */
export const buildAgenciesForRole = (agencies, role, pos_role) =>
  isClientAdminRole(role) || isClientAdminRole(pos_role) ? [] : agencies;

// Check both strings we are currently seeing for CB_Admins
export const isCBAdminRole = (role) => [CB_ADMIN, CITYBASE_ADMIN].includes(role);

// Check both strings we are currently seeing for Client Admins
export const isClientAdminRole = (role) =>
  [CLIENT_ADMIN, OKTA_CLIENT_ADMIN].includes(role);

// Handles creating an object that will take a plain object
// and transform it into the desired shape
export const transformRoleCombinations = (obj) => {
  const apiObj = typeof obj === "object" ? obj : {};
  const newObj = {};
  const keys = Object.keys(apiObj).map((key) => key.toUpperCase());

  keys.forEach((key) => {
    const originalKey = key.toLowerCase();
    const originalValue = apiObj[originalKey] || [];
    newObj[key] = originalValue.map((value) => value.toUpperCase()).reverse();
  });

  return newObj;
};

/**
 * Determines whether a user with a given role can edit user names, given an array of matching agencies.
 * @param {string} userRole - the editing user role
 * @param {string[]} matchedUserAgencies - the agencies that match between the editing user and the user being edited.
 * @return {boolean} - whether the editing user is able to change the name of the user being edited.
 */
export const userWithRoleCanEditNames = (userRole, matchedUserAgencies) => {
  if (userRole === CLIENT_ADMIN || isCBAdminRole(userRole)) {
    return true;
  } else if (userRole === AGENCY_ADMIN) {
    return Array.isArray(matchedUserAgencies) && matchedUserAgencies.length > 0;
  } else {
    return false;
  }
};
